import React, { useState, useEffect, useMemo } from "react";
import { Scale, Plus, Minus, Check, Loader2, X, Camera, Trash2, ArrowLeftRight, History, ArrowLeft } from "lucide-react";
import ProgressOverlay from "@/components/progress/ProgressOverlay";
import PoseCamera from "@/components/progress/PoseCamera";
import PoseSession from "@/components/progress/PoseSession";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { kgToLb, lbToKg, kgToStone, stoneToKg, round1 } from "@/lib/units";
import api from "@/lib/api";

// Progress photos are served behind auth, so the token rides along as a query
// param — <img src> can't carry an Authorization header.
const progressPhotoUrl = (path) => {
  const tok = localStorage.getItem("spc_token") || sessionStorage.getItem("spc_token") || "";
  return `${api.defaults.baseURL}/tracker/progress-photo/${path}?auth=${encodeURIComponent(tok)}`;
};

// Compact SVG ring — Garmin-style progress circle. Stroke transitions when
// the value updates so hitting a target feels satisfying.
function Ring({ pct = 0, color = "#E2E30A", size = 60, strokeW = 5, children }) {
  const clamped = Math.max(0, Math.min(100, pct));
  const r = (size - strokeW) / 2;
  const c = 2 * Math.PI * r;
  const dash = (clamped / 100) * c;
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth={strokeW} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={strokeW}
          strokeDasharray={`${dash} ${c - dash}`} strokeLinecap="round"
          style={{ transition: "stroke-dasharray 0.6s ease-out" }} />
      </svg>
      <div className="absolute inset-0 grid place-items-center text-center">{children}</div>
    </div>
  );
}

// Shared clickable card — pushes the editor state up so the parent owns the
// modal, keeping backdrop + centering consistent across all four metrics.
function GlanceCard({ testid, icon: Icon, label, color, ring, value, unit, sub, onClick }) {
  return (
    <button data-testid={testid} type="button" onClick={onClick}
      className="w-full border border-white/10 bg-[#141414] hover:bg-white/[0.03] hover:border-white/25 focus:outline-none focus-visible:border-[#E2E30A]/60 p-3 sm:p-4 flex items-center gap-3 text-left transition-colors"
      style={{ boxShadow: `inset 3px 0 0 0 ${color}` }}>
      {ring !== null && ring !== undefined ? (
        <Ring pct={ring} color={color} size={60} strokeW={5}>
          <Icon className="h-4 w-4" style={{ color }} />
        </Ring>
      ) : (
        <div className="h-14 w-14 grid place-items-center border shrink-0" style={{ borderColor: `${color}55`, background: `${color}11` }}>
          <Icon className="h-5 w-5" style={{ color }} />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 font-bold truncate">{label}</p>
        <p className="font-display text-xl sm:text-2xl leading-none text-white mt-1 truncate">
          {value}<span className="text-xs text-zinc-400 ml-1 font-mono">{unit}</span>
        </p>
        {sub && <p className="text-[10px] font-mono text-zinc-400 mt-1 truncate">{sub}</p>}
      </div>
    </button>
  );
}

function WeightEditor({ initial, unit: initialUnit, color, goalKg, onSave, onDone }) {
  // `initial` is stored kg. Athletes can flip kg/lb/st inline here (beta
  // feedback Feb 26 2026): some prefer stones for a home scale, others
  // train in kg. The selection persists to localStorage so the next
  // weigh-in opens in the same unit without touching Account Settings.
  const [unit, setUnit] = useState(() => {
    try {
      const saved = localStorage.getItem("spc-weight-unit-editor");
      if (saved === "kg" || saved === "lb" || saved === "st") return saved;
    } catch { /* noop */ }
    return initialUnit || "kg";
  });
  const initialLb = initial != null ? kgToLb(initial) : null;
  const initialSt = initial != null ? kgToStone(initial) : null;
  const [kg, setKg] = useState(initial ?? "");
  const [lb, setLb] = useState(initialLb != null ? round1(initialLb) : "");
  const [st, setSt] = useState(initialSt?.st ?? "");
  const [stLb, setStLb] = useState(initialSt?.lb ?? "");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    const s = initial != null ? kgToStone(initial) : null;
    setKg(initial ?? "");
    setLb(initial != null ? round1(kgToLb(initial)) : "");
    setSt(s?.st ?? "");
    setStLb(s?.lb ?? "");
  }, [initial]);
  // When the athlete flips the unit, carry the current typed value across
  // so they don't lose input mid-entry.
  const setUnitPersist = (next) => {
    if (next === unit) return;
    let currentKg = null;
    if (unit === "kg" && kg !== "") currentKg = parseFloat(kg);
    else if (unit === "lb" && lb !== "") currentKg = lbToKg(parseFloat(lb));
    else if (unit === "st" && (st !== "" || stLb !== "")) currentKg = stoneToKg(st, stLb);
    if (currentKg != null && isFinite(currentKg) && currentKg > 0) {
      if (next === "kg") setKg(round1(currentKg));
      if (next === "lb") setLb(round1(kgToLb(currentKg)));
      if (next === "st") { const s2 = kgToStone(currentKg); setSt(s2.st); setStLb(s2.lb); }
    }
    setUnit(next);
    try { localStorage.setItem("spc-weight-unit-editor", next); } catch { /* noop */ }
  };

  const submit = async () => {
    let outKg = null;
    if (unit === "kg" && kg !== "") outKg = round1(parseFloat(kg));
    else if (unit === "lb" && lb !== "") outKg = round1(lbToKg(parseFloat(lb)));
    else if (unit === "st" && (st !== "" || stLb !== "")) outKg = stoneToKg(st, stLb);
    if (outKg != null && (!isFinite(outKg) || outKg <= 0)) outKg = null;
    setSaving(true);
    try { await onSave(outKg); onDone(); }
    finally { setSaving(false); }
  };
  const clear = async () => {
    setSaving(true);
    try { await onSave(null); onDone(); }
    finally { setSaving(false); }
  };

  const goalLine = goalKg
    ? (unit === "lb" ? `Goal: ${round1(kgToLb(goalKg))} lb`
      : unit === "st" ? (() => { const g = kgToStone(goalKg); return `Goal: ${g.st}st ${g.lb}lb`; })()
      : `Goal: ${goalKg} kg`)
    : "Weigh first thing in the morning for a clean trend.";

  return (
    <div className="space-y-4">
      <div data-testid="weight-editor-unit-toggle" className="grid grid-cols-3 gap-1 border border-white/10 bg-black p-1">
        {[
          { k: "kg", label: "Kilos" },
          { k: "lb", label: "Pounds" },
          { k: "st", label: "Stone" },
        ].map((u) => (
          <button key={u.k} type="button" data-testid={`weight-unit-${u.k}`}
            onClick={() => setUnitPersist(u.k)}
            className={`py-1.5 text-[11px] font-bold uppercase tracking-wider transition-colors ${
              unit === u.k
                ? "bg-[#E2E30A] text-black"
                : "text-zinc-400 hover:text-white"
            }`}>{u.label}</button>
        ))}
      </div>
      {unit === "kg" && (
        <div className="flex items-center gap-2">
          <Input data-testid="weight-editor-kg" type="number" inputMode="decimal" step="0.1"
            value={kg} onChange={(e) => setKg(e.target.value)} placeholder="e.g. 72.5" autoFocus
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submit(); } }}
            className="bg-black border-white/15 text-white text-2xl font-display h-14 text-center" />
          <span className="text-sm font-mono uppercase tracking-wider text-zinc-400 shrink-0 w-10">kg</span>
        </div>
      )}
      {unit === "lb" && (
        <div className="flex items-center gap-2">
          <Input data-testid="weight-editor-lb" type="number" inputMode="decimal" step="0.1"
            value={lb} onChange={(e) => setLb(e.target.value)} placeholder="e.g. 160.5" autoFocus
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submit(); } }}
            className="bg-black border-white/15 text-white text-2xl font-display h-14 text-center" />
          <span className="text-sm font-mono uppercase tracking-wider text-zinc-400 shrink-0 w-10">lb</span>
        </div>
      )}
      {unit === "st" && (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] text-zinc-400 font-bold mb-1.5">Stone</p>
            <Input data-testid="weight-editor-st" type="number" inputMode="numeric" step="1" min="0"
              value={st} onChange={(e) => setSt(e.target.value)} placeholder="11" autoFocus
              className="bg-black border-white/15 text-white text-2xl font-display h-14 text-center" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] text-zinc-400 font-bold mb-1.5">Pounds</p>
            <Input data-testid="weight-editor-stlb" type="number" inputMode="numeric" step="1" min="0" max="13"
              value={stLb} onChange={(e) => setStLb(e.target.value)} placeholder="6"
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submit(); } }}
              className="bg-black border-white/15 text-white text-2xl font-display h-14 text-center" />
          </div>
        </div>
      )}
      <p className="text-[11px] text-zinc-400">{goalLine}</p>
      <div className="flex items-center gap-2">
        <button type="button" data-testid="weight-editor-save" onClick={submit} disabled={saving}
          className="flex-1 inline-flex items-center justify-center gap-1.5 border-2 font-bold uppercase tracking-wider text-xs px-3 py-3 transition-colors disabled:opacity-60"
          style={{ borderColor: color, color: "#000", background: color }}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="h-4 w-4" /> Save</>}
        </button>
        {initial != null && (
          <button type="button" data-testid="weight-editor-clear" onClick={clear} disabled={saving}
            className="px-4 py-3 border border-white/15 text-zinc-300 hover:text-[#FF3B30] hover:border-[#FF3B30]/50 text-xs uppercase tracking-wider transition-colors">
            Clear
          </button>
        )}
        <button type="button" onClick={onDone}
          className="px-4 py-3 border border-white/15 text-zinc-300 hover:text-white hover:border-white/30 text-xs uppercase tracking-wider transition-colors">
          Cancel
        </button>
      </div>
    </div>
  );
}

function formatWeight(kg, unit) {
  if (kg == null) return null;
  if (unit === "lb") return `${round1(kgToLb(kg))} lb`;
  if (unit === "st") {
    const s = kgToStone(kg);
    return `${s.st}st ${s.lb}`;
  }
  return `${round1(kg)} kg`;
}
// Format a weight difference in the athlete's preferred unit (signed).
function formatWeightDelta(kg, unit) {
  if (kg == null) return null;
  if (unit === "lb") {
    const lb = round1(kgToLb(Math.abs(kg))) * (kg < 0 ? -1 : 1);
    return `${lb > 0 ? "+" : ""}${lb} lb`;
  }
  if (unit === "st") {
    const totalLb = kgToLb(Math.abs(kg));
    const sign = kg >= 0 ? "+" : "-";
    const st = Math.floor(totalLb / 14);
    const lbRem = Math.round(totalLb - st * 14);
    return `${sign}${st}st ${lbRem}lb`;
  }
  const v = round1(kg);
  return `${v > 0 ? "+" : ""}${v} kg`;
}

// Timeline / Compare view — pick any two progress photos and see them
// side-by-side with a "N days apart" delta. Defaults to earliest vs latest
// (classic "before / after"). Range chips filter which photos are pickable.
function ProgressTimeline({ photos, onBack, weightUnit = "kg" }) {
  const sorted = useMemo(
    () => [...(photos || [])].sort((a, b) => a.date.localeCompare(b.date)),
    [photos]);
  const [range, setRange] = useState("all"); // week | month | 3m | 6m | all
  const [poseFilter, setPoseFilter] = useState("all"); // all | front | side | back | custom
  const [activeSlot, setActiveSlot] = useState("after"); // "before" | "after"
  const [beforeId, setBeforeId] = useState(null);
  const [afterId, setAfterId] = useState(null);

  // Auto-pick earliest + latest as before/after the first time we have
  // photos, and any time the pool shrinks below the current selection.
  useEffect(() => {
    if (!sorted.length) { setBeforeId(null); setAfterId(null); return; }
    const ids = new Set(sorted.map((p) => p.id));
    const nextBefore = beforeId && ids.has(beforeId) ? beforeId : sorted[0].id;
    const nextAfter = afterId && ids.has(afterId) ? afterId : sorted[sorted.length - 1].id;
    if (nextBefore !== beforeId) setBeforeId(nextBefore);
    if (nextAfter !== afterId) setAfterId(nextAfter);
  }, [sorted, beforeId, afterId]);

  const cutoffDate = useMemo(() => {
    if (range === "all") return null;
    const days = { week: 7, month: 30, "3m": 90, "6m": 180 }[range] || 0;
    if (!days) return null;
    const d = new Date();
    d.setDate(d.getDate() - days);
    return d.toISOString().slice(0, 10);
  }, [range]);

  const filtered = useMemo(() => {
    let out = sorted;
    if (cutoffDate) out = out.filter((p) => p.date >= cutoffDate);
    if (poseFilter !== "all") out = out.filter((p) => (p.pose || "front") === poseFilter);
    return out;
  }, [sorted, cutoffDate, poseFilter]);

  const findById = (id) => sorted.find((p) => p.id === id) || null;
  const before = findById(beforeId);
  const after = findById(afterId);
  const daysApart = (() => {
    if (!before || !after) return null;
    const a = new Date(before.date + "T00:00:00");
    const b = new Date(after.date + "T00:00:00");
    return Math.round((b - a) / 86400000);
  })();

  const swap = () => { setBeforeId(afterId); setAfterId(beforeId); };
  const assignToActive = (id) => {
    if (activeSlot === "before") { setBeforeId(id); setActiveSlot("after"); }
    else { setAfterId(id); setActiveSlot("before"); }
  };
  const fmtDate = (iso) => {
    try {
      return new Date(iso + "T00:00:00").toLocaleDateString(undefined,
        { day: "numeric", month: "short", year: "numeric" });
    } catch { return iso; }
  };
  // Weight change between the two slots — only shown when both photos have
  // a weight attached (either exact-day log or nearest weigh-in ±3d).
  const weightDeltaKg = (before?.weightKg != null && after?.weightKg != null)
    ? +(after.weightKg - before.weightKg).toFixed(1) : null;

  return (
    <div className="space-y-4">
      <button type="button" data-testid="progress-timeline-back" onClick={onBack}
        className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-zinc-300 hover:text-white transition-colors">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to today
      </button>

      {sorted.length < 2 ? (
        <div className="border border-dashed border-white/15 py-10 text-center text-sm text-zinc-400" data-testid="progress-timeline-empty">
          <History className="h-6 w-6 mx-auto text-zinc-500 mb-2" />
          Log at least two progress photos to unlock the before/after comparison.
        </div>
      ) : (
        <>
          {/* Overlay — drag the line across the two photos */}
          {before && after && (
            <ProgressOverlay before={before} after={after} urlFor={progressPhotoUrl}
              weightUnit={weightUnit} />
          )}

          {/* Compare slots — before / after side-by-side */}
          <div className="grid grid-cols-2 gap-3" data-testid="progress-timeline-compare">
            {[
              { key: "before", label: "Before", photo: before, colour: "#4C9AFF" },
              { key: "after",  label: "After",  photo: after,  colour: "#FB7185" },
            ].map(({ key, label, photo, colour }) => (
              <button key={key} type="button"
                data-testid={`progress-timeline-slot-${key}`}
                onClick={() => setActiveSlot(key)}
                className={`text-left border-2 overflow-hidden transition-colors ${activeSlot === key ? "" : "border-white/10 hover:border-white/25"}`}
                style={activeSlot === key ? { borderColor: colour } : {}}>
                {photo ? (
                  <div className="aspect-[3/4] bg-black relative">
                    <img src={progressPhotoUrl(photo.photoPath)} alt={label} className="w-full h-full object-cover" />
                    {/* Top-left pose badge */}
                    <span className="absolute top-2 left-2 text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 bg-black/70 text-white border border-white/15">
                      {photo.pose || "front"}
                    </span>
                    {/* Top-right stat stack */}
                    <div className="absolute top-2 right-2 flex flex-col items-end gap-1">
                      {photo.weightKg != null && (
                        <span data-testid={`progress-timeline-weight-${key}`}
                          className="text-[10px] font-mono font-bold px-1.5 py-0.5 bg-[#E2E30A] text-black">
                          {formatWeight(photo.weightKg, weightUnit)}
                        </span>
                      )}
                      {photo.weightAvg7Kg != null && photo.weightAvg7Kg !== photo.weightKg && (
                        <span className="text-[9px] font-mono px-1.5 py-0.5 bg-black/70 text-zinc-100 border border-white/15">
                          {formatWeight(photo.weightAvg7Kg, weightUnit)} · 7d avg
                        </span>
                      )}
                    </div>
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 to-transparent px-2 py-1.5">
                      <p className="text-[9px] uppercase tracking-[0.22em] font-bold" style={{ color: colour }}>{label}</p>
                      <p className="text-[11px] text-white font-mono">{fmtDate(photo.date)}</p>
                      {photo.note && <p className="text-[10px] text-zinc-300 truncate">{photo.note}</p>}
                    </div>
                  </div>
                ) : (
                  <div className="aspect-[3/4] grid place-items-center text-[11px] text-zinc-500 uppercase tracking-wider">
                    Pick a photo
                  </div>
                )}
              </button>
            ))}
          </div>

          {/* Delta banner — days apart + weight Δ + swap */}
          {before && after && (
            <div className="flex flex-wrap items-center gap-2 border border-white/10 bg-black/40 px-3 py-2" data-testid="progress-timeline-delta">
              <p className="text-[11px] font-mono text-zinc-300">
                {daysApart === 0 ? "Same day" : `${Math.abs(daysApart)} day${Math.abs(daysApart) === 1 ? "" : "s"} ${daysApart >= 0 ? "apart" : "reversed"}`}
              </p>
              {weightDeltaKg != null && (
                <span data-testid="progress-timeline-weight-delta"
                  className="text-[10px] font-mono font-bold px-1.5 py-0.5 border"
                  style={{
                    color: "#F5F1E8",
                    background: "transparent",
                    borderColor: weightDeltaKg === 0 ? "#9ca3af" : weightDeltaKg < 0 ? "#34C759" : "#FF9500",
                  }}>
                  {weightDeltaKg === 0 ? "= weight" : `${formatWeightDelta(weightDeltaKg, weightUnit)} weight`}
                </span>
              )}
              <button type="button" data-testid="progress-timeline-swap" onClick={swap}
                className="ml-auto inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-zinc-300 hover:text-white transition-colors">
                <ArrowLeftRight className="h-3.5 w-3.5" /> Swap
              </button>
            </div>
          )}

          {/* Filters — range + pose */}
          <div className="space-y-2">
            <p className="text-[10px] uppercase tracking-[0.22em] text-zinc-400">
              Filter · picking for <span style={{ color: activeSlot === "before" ? "#4C9AFF" : "#FB7185" }} className="font-bold">{activeSlot === "before" ? "Before" : "After"}</span>
            </p>
            <div className="flex flex-wrap gap-2">
              <div className="inline-flex flex-wrap gap-1 border border-white/10 p-1" data-testid="progress-timeline-range">
                {[
                  { k: "week", label: "7d" },
                  { k: "month", label: "30d" },
                  { k: "3m", label: "3mo" },
                  { k: "6m", label: "6mo" },
                  { k: "all", label: "All" },
                ].map((o) => (
                  <button key={o.k} type="button" data-testid={`progress-timeline-range-${o.k}`}
                    onClick={() => setRange(o.k)}
                    className={`px-2.5 py-1 text-[10px] uppercase tracking-wider font-semibold transition-colors ${range === o.k ? "bg-[#E2E30A] text-black" : "text-zinc-300 hover:text-white"}`}>
                    {o.label}
                  </button>
                ))}
              </div>
              <div className="inline-flex flex-wrap gap-1 border border-white/10 p-1" data-testid="progress-timeline-pose-filter">
                {[
                  { k: "all", label: "All" },
                  { k: "front", label: "Front" },
                  { k: "side", label: "Side" },
                  { k: "back", label: "Back" },
                  { k: "custom", label: "Custom" },
                ].map((o) => (
                  <button key={o.k} type="button" data-testid={`progress-timeline-pose-${o.k}`}
                    onClick={() => setPoseFilter(o.k)}
                    className={`px-2.5 py-1 text-[10px] uppercase tracking-wider font-semibold transition-colors ${poseFilter === o.k ? "bg-[#FB7185] text-black" : "text-zinc-300 hover:text-white"}`}>
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Strip — chronological grid of all photos in the range */}
          {filtered.length === 0 ? (
            <div className="border border-dashed border-white/15 py-6 text-center text-[11px] text-zinc-400" data-testid="progress-timeline-range-empty">
              No photos in the selected range.
            </div>
          ) : (
            <div className="grid grid-cols-4 sm:grid-cols-6 gap-1.5 max-h-64 overflow-y-auto pr-1" data-testid="progress-timeline-strip">
              {filtered.map((p) => {
                const isBefore = p.id === beforeId;
                const isAfter = p.id === afterId;
                const activeColour = isBefore ? "#4C9AFF" : isAfter ? "#FB7185" : null;
                return (
                  <button key={p.id} type="button"
                    data-testid={`progress-timeline-pick-${p.id}`}
                    onClick={() => assignToActive(p.id)}
                    className="relative aspect-square overflow-hidden border-2 hover:opacity-90 transition-opacity"
                    style={activeColour ? { borderColor: activeColour } : { borderColor: "rgba(255,255,255,0.08)" }}
                    title={`${fmtDate(p.date)} · ${p.pose || "front"}${p.weightKg != null ? " · " + formatWeight(p.weightKg, weightUnit) : ""}${p.note ? " · " + p.note : ""}`}>
                    <img src={progressPhotoUrl(p.photoPath)} alt="progress" className="w-full h-full object-cover" />
                    {(isBefore || isAfter) && (
                      <span className="absolute top-1 left-1 text-[8px] font-bold uppercase tracking-widest px-1 py-0.5 text-black"
                        style={{ background: activeColour }}>
                        {isBefore ? "B" : "A"}
                      </span>
                    )}
                    {!(isBefore || isAfter) && (
                      <span className="absolute top-1 left-1 text-[7px] font-bold uppercase tracking-widest px-1 py-0.5 bg-black/70 text-white/90 border border-white/10">
                        {(p.pose || "front").slice(0, 1)}
                      </span>
                    )}
                    <span className="absolute inset-x-0 bottom-0 bg-black/70 text-[8px] text-white font-mono px-1 py-0.5 truncate">
                      {p.date.slice(5)}{p.weightKg != null ? ` · ${formatWeight(p.weightKg, weightUnit).replace(" ", "")}` : ""}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ProgressPhotoEditor({ date, todayPhotos, totalCount, allPhotos = [], onUploaded, onDeleted, onOpenTimeline, onDone, weightUnit = "kg" }) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [pose, setPose] = useState("front");
  const [camera, setCamera] = useState(false);
  const [session, setSession] = useState(false);
  const inputRef = React.useRef(null);
  // Most recent photo of the chosen pose — ghosted over the live camera so the
  // athlete stands in the same place each time.
  const ghostPhoto = useMemo(() => {
    const same = (allPhotos || [])
      .filter((p) => (p.pose || "front") === pose)
      .sort((a, b) => b.date.localeCompare(a.date));
    return same[0] || null;
  }, [allPhotos, pose]);
  const upload = async (file, poseOverride) => {
    if (!file) return false;
    if (file.size > 8 * 1024 * 1024) {
      const { toast } = await import("sonner");
      toast.error("Photo too large (8MB max)");
      return false;
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("date", date);
      form.append("note", note.trim());
      form.append("pose", poseOverride || pose);
      const { data } = await api.post("/tracker/progress-photo", form,
        { headers: { "Content-Type": "multipart/form-data" } });
      onUploaded && onUploaded(data);
      setNote("");
      return true;
    } catch (e) {
      const { toast } = await import("sonner");
      toast.error(e?.response?.data?.detail || "Upload failed");
      return false;
    } finally {
      setBusy(false);
    }
  };
  const ghostFor = (p) => {
    const last = (allPhotos || [])
      .filter((x) => (x.pose || "front") === p)
      .sort((a, b) => b.date.localeCompare(a.date))[0];
    return last ? progressPhotoUrl(last.photoPath) : null;
  };
  const posesToday = new Set(todayPhotos.map((p) => p.pose || "front"));
  const missingToday = ["front", "side", "back"].filter((p) => !posesToday.has(p));
  const deletePhoto = async (id) => {
    if (!id) return;
    setBusy(true);
    try {
      await api.delete(`/tracker/progress-photo/${id}`);
      onDeleted && onDeleted(id);
    } catch {
      const { toast } = await import("sonner");
      toast.error("Could not delete");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-4">
      {session ? (
        <PoseSession ghostFor={ghostFor} onUpload={upload} onDone={() => setSession(false)} />
      ) : (
        <>
          {camera && (
            <PoseCamera pose={pose}
              ghostUrl={ghostPhoto ? progressPhotoUrl(ghostPhoto.photoPath) : null}
              onCancel={() => setCamera(false)}
              onCapture={async (file) => { setCamera(false); await upload(file); }} />
          )}
          <button type="button" data-testid="progress-start-session" onClick={() => setSession(true)}
            disabled={busy || camera}
            className="w-full flex items-center justify-between gap-2 border-2 border-[#E2E30A]/50 hover:border-[#E2E30A] bg-[#E2E30A]/[0.06] px-3 py-2.5 transition-colors disabled:opacity-60">
            <span className="inline-flex items-center gap-2">
              <Camera className="h-4 w-4 text-[#E2E30A]" />
              <span className="text-[11px] uppercase tracking-[0.22em] font-bold text-white">Shoot full set · front, side, back</span>
            </span>
            <span className="text-[10px] font-mono text-zinc-400">
              {missingToday.length === 0 ? "Complete today" : `${3 - missingToday.length}/3 today`}
            </span>
          </button>
        </>
      )}
      {(totalCount || 0) >= 2 && (
        <button type="button" data-testid="progress-open-timeline" onClick={onOpenTimeline}
          className="w-full flex items-center justify-between gap-2 border border-white/10 hover:border-[#FB7185]/60 hover:bg-white/[0.03] px-3 py-2.5 transition-colors group">
          <span className="inline-flex items-center gap-2">
            <History className="h-4 w-4 text-[#FB7185]" />
            <span className="text-[11px] uppercase tracking-[0.22em] font-bold text-white">View timeline &amp; compare</span>
          </span>
          <span className="text-[10px] font-mono text-zinc-400 group-hover:text-white transition-colors">{totalCount} photo{totalCount === 1 ? "" : "s"}</span>
        </button>
      )}
      <div>
        <p className="text-[10px] uppercase tracking-[0.22em] text-zinc-400 mb-2">Today · {todayPhotos.length} photo{todayPhotos.length === 1 ? "" : "s"}</p>
        {todayPhotos.length > 0 ? (
          <div className="grid grid-cols-3 gap-2">
            {todayPhotos.map((p) => (
              <div key={p.id} className="relative group aspect-square border border-white/10 overflow-hidden">
                <img data-testid={`progress-photo-thumb-${p.id}`}
                  src={progressPhotoUrl(p.photoPath)} alt="progress"
                  className="w-full h-full object-cover" />
                <span className="absolute top-1 left-1 text-[8px] font-bold uppercase tracking-widest px-1 py-0.5 bg-black/70 text-white border border-white/15">
                  {p.pose || "front"}
                </span>
                {p.weightKg != null && (
                  <span className="absolute bottom-1 left-1 text-[9px] font-mono px-1 py-0.5 bg-[#E2E30A] text-black font-bold">
                    {formatWeight(p.weightKg, weightUnit)}
                  </span>
                )}
                <button type="button" data-testid={`progress-photo-delete-${p.id}`}
                  onClick={() => deletePhoto(p.id)} disabled={busy}
                  className="absolute top-1 right-1 bg-black/70 border border-white/20 p-1 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity hover:bg-[#FF3B30]/80">
                  <Trash2 className="h-3 w-3 text-white" />
                </button>
                {p.note && <p className="absolute bottom-0 inset-x-0 bg-black/70 text-[9px] text-white px-1.5 py-0.5 truncate">{p.note}</p>}
              </div>
            ))}
          </div>
        ) : (
          <div className="border border-dashed border-white/15 py-6 text-center text-[11px] text-zinc-400">
            No photos yet for today.
          </div>
        )}
      </div>

      <div>
        <label className="block text-[10px] uppercase tracking-[0.22em] text-zinc-400 mb-2">Pose</label>
        <div className="grid grid-cols-4 gap-1.5" data-testid="progress-pose-picker">
          {["front", "side", "back", "custom"].map((p) => (
            <button key={p} type="button" data-testid={`progress-pose-${p}`}
              onClick={() => setPose(p)}
              className={`py-1.5 text-[10px] uppercase tracking-wider font-semibold border transition-colors ${pose === p
                ? "border-[#FB7185] text-black bg-[#FB7185]"
                : "border-white/15 text-zinc-300 hover:text-white hover:border-white/30"}`}>
              {p}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <label className="block text-[10px] uppercase tracking-[0.22em] text-zinc-400">Optional note (max 200 chars)</label>
        <Input data-testid="progress-photo-note" value={note} onChange={(e) => setNote(e.target.value.slice(0, 200))}
          placeholder="e.g. Post-run, morning fasted, race week" maxLength={200}
          className="bg-black border-white/15 text-white h-9 text-sm" />
      </div>

      <input ref={inputRef} type="file" accept="image/*" capture="environment" className="hidden"
        data-testid="progress-photo-input"
        onChange={(e) => upload(e.target.files?.[0])} disabled={busy} />
      <div className="grid grid-cols-2 gap-2">
        <button type="button" data-testid="progress-photo-camera"
          onClick={() => setCamera(true)}
          disabled={busy || camera}
          className="h-11 bg-[#E2E30A] hover:bg-[#c9ca09] disabled:opacity-60 text-black text-[11px] uppercase font-bold tracking-wider inline-flex items-center justify-center gap-1.5">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" />
                : <><Camera className="h-4 w-4" /> {ghostPhoto ? "Camera + guide" : "Take photo"}</>}
        </button>
        <button type="button" data-testid="progress-photo-upload"
          onClick={() => { if (inputRef.current) { inputRef.current.removeAttribute("capture"); inputRef.current.click(); } }}
          disabled={busy}
          className="h-11 border border-white/15 hover:border-white/40 disabled:opacity-60 text-white text-[11px] uppercase font-bold tracking-wider inline-flex items-center justify-center gap-1.5">
          Upload from library
        </button>
      </div>
      <button type="button" onClick={onDone}
        className="w-full py-2.5 border border-white/10 text-zinc-400 hover:text-white text-xs uppercase tracking-wider transition-colors">
        Done
      </button>
    </div>
  );
}

// Garmin Connect-style At-a-Glance grid — tap any card to edit that metric
// in a centered modal with a dimmed backdrop.
export default function AtAGlanceGrid({
  note, weightKg, weightGoalKg, weightUnit = "kg",
  onWeightSave, date, weightTracking = "weekly",
  progressPhotosEnabled = false, progressPhotos = [],
  onProgressPhotoUploaded, onProgressPhotoDeleted,
}) {
  const [editing, setEditing] = useState(null); // "weight" | "progress" | "progress-timeline" | null
  // Beta feedback (Feb 26 2026): "some beta testers have said they want to
  // track weight daily and others don't". Athletes pick their cadence in
  // Account Settings → Wellness. "weekly" (default, Sat/Sun only) keeps
  // the trigger-warning safety net; "daily" opens it every day for the
  // athletes who want a Whoop-style weigh-in; "off" hides the card entirely.
  const showWeight = (() => {
    if (weightTracking === "off") return false;
    if (weightTracking === "daily") return true;
    try {
      const d = date ? new Date(date + "T12:00:00") : new Date();
      const dow = d.getDay(); // 0 Sun, 6 Sat
      return dow === 0 || dow === 6;
    } catch { return false; }
  })();
  // Weight display + delta rendered in the athlete's preferred unit.
  const weightDisplay = (() => {
    if (weightKg == null) return { value: "—", unit: weightUnit === "st" ? "" : weightUnit };
    if (weightUnit === "lb") return { value: round1(kgToLb(weightKg)).toString(), unit: "lb" };
    if (weightUnit === "st") { const s = kgToStone(weightKg); return { value: `${s.st}st ${s.lb}`, unit: "lb" }; }
    return { value: round1(weightKg).toString(), unit: "kg" };
  })();
  const weightDeltaLine = (() => {
    if (weightKg == null || !weightGoalKg) return null;
    if (weightUnit === "lb") {
      const d = round1(kgToLb(weightKg) - kgToLb(weightGoalKg));
      return `${d > 0 ? "+" : ""}${d}lb vs goal`;
    }
    if (weightUnit === "st") {
      const totalLb = kgToLb(weightKg) - kgToLb(weightGoalKg);
      const sign = totalLb >= 0 ? "+" : "-";
      const abs = Math.abs(totalLb);
      const st = Math.floor(abs / 14);
      const lb = Math.round(abs - st * 14);
      return `${sign}${st}st ${lb}lb vs goal`;
    }
    const d = +(weightKg - weightGoalKg).toFixed(1);
    return `${d > 0 ? "+" : ""}${d}kg vs goal`;
  })();
  const close = () => setEditing(null);

  const META = {
    weight: { icon: Scale, label: "Weight", color: "#E2E30A" },
    progress: { icon: Camera, label: "Progress Photo", color: "#FB7185" },
    "progress-timeline": { icon: History, label: "Photo Timeline", color: "#FB7185" },
  };
  const activeMeta = editing ? META[editing] : null;
  const ActiveIcon = activeMeta?.icon;
  const todayPhotos = (progressPhotos || []).filter((p) => p.date === date);
  // Tile count decides the grid layout — 3 base tiles + optional Weight + optional Progress.
  const tiles = (showWeight ? 1 : 0) + (progressPhotosEnabled ? 1 : 0);
  const gridCols = tiles === 2 ? "lg:grid-cols-2" : "lg:grid-cols-1";

  return (
    <>
      <section data-testid="at-a-glance-grid" className={`grid grid-cols-2 ${gridCols} gap-2.5 sm:gap-3`}>
        {showWeight && (
          <GlanceCard testid="glance-weight" icon={Scale} label="Weight" color="#E2E30A" ring={null}
            value={weightDisplay.value} unit={weightDisplay.unit}
            sub={weightKg != null && weightDeltaLine
              ? `Weekly check-in · ${weightDeltaLine}`
              : (weightKg != null ? "Weekly check-in · tap to edit" : "Weekly check-in · tap to log")}
            onClick={() => setEditing("weight")} />
        )}
        {progressPhotosEnabled && (
          <GlanceCard testid="glance-progress-photo" icon={Camera} label="Progress" color="#FB7185" ring={null}
            value={todayPhotos.length > 0 ? String(todayPhotos.length) : "—"}
            unit={todayPhotos.length > 0 ? (todayPhotos.length === 1 ? "photo" : "photos") : ""}
            sub={todayPhotos.length > 0 ? "Tap to add another or delete" : "Tap to add today's photo"}
            onClick={() => setEditing("progress")} />
        )}
      </section>

      <Dialog open={!!editing} onOpenChange={(o) => !o && close()}>
        <DialogContent data-testid={`glance-editor-${editing || ""}`}
          className={`force-dark bg-[#141414] border-white/15 text-white ${editing === "progress-timeline" ? "sm:max-w-2xl" : "sm:max-w-sm"} rounded-none p-0 gap-0 max-h-[92vh] overflow-y-auto`}>
          {activeMeta && (
            <>
              <DialogHeader className="px-5 pt-5 pb-3 border-b border-white/10">
                <DialogTitle className="text-xs font-bold uppercase tracking-[0.22em] text-white flex items-center gap-2">
                  <ActiveIcon className="h-4 w-4" style={{ color: activeMeta.color }} />
                  {activeMeta.label}
                </DialogTitle>
                <DialogDescription className="sr-only">Edit your {activeMeta.label.toLowerCase()} for today.</DialogDescription>
              </DialogHeader>
              <div className="p-5">
                {editing === "weight" && (
                  <WeightEditor initial={weightKg} unit={weightUnit} color="#E2E30A"
                    goalKg={weightGoalKg} onSave={onWeightSave} onDone={close} />
                )}
                {editing === "progress" && (
                  <ProgressPhotoEditor
                    date={date}
                    todayPhotos={todayPhotos}
                    totalCount={(progressPhotos || []).length}
                    allPhotos={progressPhotos || []}
                    weightUnit={weightUnit}
                    onUploaded={onProgressPhotoUploaded}
                    onDeleted={onProgressPhotoDeleted}
                    onOpenTimeline={() => setEditing("progress-timeline")}
                    onDone={close}
                  />
                )}
                {editing === "progress-timeline" && (
                  <ProgressTimeline
                    photos={progressPhotos || []}
                    weightUnit={weightUnit}
                    onBack={() => setEditing("progress")}
                  />
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
